import { formatNumber, truncateAddress } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { memo, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Currency from 'components/Currency'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import { ToolTipTrigger } from 'components/PnlTooltip'
import PnlTooltip from 'components/PnlTooltip'
import Table, { TableHeader, TableNoResults as BaseTableNoResults } from 'components/Table'
import { TableCell } from 'components/Table/TableBodyRow'
import { Body } from 'components/Text'
import Tooltip from 'components/Tooltip/Tooltip'
import { FlexDivCol, FlexDivRow } from 'components/layout/flex'
import { NO_VALUE } from 'constants/placeholder'
import { blockExplorer } from 'containers/Connector/Connector'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectTradesQueryStatus, selectUserInfoTradeHistory } from 'state/futures/selectors'
import type { TradeHistory } from 'state/futures/types'
import { useAppSelector } from 'state/hooks'
import { FetchStatus } from 'state/types'
import { ExternalLink } from 'styles/common'
import { providerIsCrossMargin } from 'utils/futures'

import MobileTableFilters from '../MobileTrade/UserTabs/TableFilters'
import PositionType from '../PositionType'
import TimeDisplay from '../Trades/TimeDisplay'
import TableFilters from '../UserInfo/TableFilters/TableFilters'
import { FuturesTab } from '../UserInfo/UserInfoTabs'
import { USER_INFO_FILTERS_HEIGHT } from '../styles'

const TradesTable = memo(() => {
	const { t } = useTranslation()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const trades = useAppSelector(selectUserInfoTradeHistory, (a, b) => a.length === b.length)
	const tradesStatus = useAppSelector(selectTradesQueryStatus)
	const getTooltipData = useCallback(
		(trade: TradeHistory) => ({
			pnl: trade.pnl || '0',
			feesPaid: trade.feesPaid,
			funding: trade.funding || '0',
			interestCharged: trade.interestCharged,
			totalPnl: trade.netPnl || '0',
			translationKey: 'trades' as const,
		}),
		[]
	)

	const isLoading = useMemo(() => {
		return !trades.length && tradesStatus === FetchStatus.Loading
	}, [tradesStatus, trades])

	const columnsDeps = useMemo(() => [trades], [trades])
	const containerRef = useRef<HTMLDivElement>(null)

	return (
		<Container ref={containerRef}>
			<DesktopOnlyView>
				<TableContainer ref={containerRef}>
					<TableFilters tab={FuturesTab.TRADES} />
					<Table
						containerHeight={containerRef.current?.clientHeight}
						rowHeight={42}
						useVirtual
						highlightRowsOnHover
						rounded={false}
						noBottom={false}
						autoResetPageIndex={false}
						columnVisibility={{ committedTxHash: providerIsCrossMargin(perpsProvider) }}
						showPagination={false}
						columns={[
							{
								header: () => <TableHeader>{t('futures.market.user.trades.date')}</TableHeader>,
								accessorKey: 'timestamp',
								cell: (cellProps) => <TimeDisplay horizontal value={cellProps.getValue()} />,
								enableSorting: true,
							},
							{
								header: () => <TableHeader>{t('futures.market.user.trades.asset')}</TableHeader>,
								accessorKey: 'displayAsset',
								cell: (cellProps) => <Body>{cellProps.getValue()}</Body>,
								enableSorting: true,
								maxSize: 60,
							},
							{
								header: () => <TableHeader>{t('futures.market.user.trades.side')}</TableHeader>,
								accessorKey: 'side',
								cell: (cellProps) => {
									return cellProps.row.original.side ? (
										<PositionType side={cellProps.row.original.side} />
									) : (
										NO_VALUE
									)
								},
								maxSize: 60,
								enableSorting: true,
							},
							{
								header: () => <TableHeader>{t('futures.market.user.trades.price')}</TableHeader>,
								accessorKey: 'price',
								cell: (cellProps) => {
									return <Currency.Price price={cellProps.getValue()} />
								},
								enableSorting: true,
							},
							{
								header: () => <TableHeader>{t('futures.market.user.trades.quantity')}</TableHeader>,
								accessorKey: 'amount',
								cell: (cellProps) => {
									return (
										<FlexDivRow>
											{formatNumber(cellProps.getValue(), {
												suggestDecimals: true,
												removeTrailingZeros: true,
											})}
											{` ${cellProps.row.original.displayAsset}`}
											{cellProps.row.original.orderType === 'Liquidation' ? ' 💀' : ''}
										</FlexDivRow>
									)
								},
								enableSorting: true,
							},
							{
								header: () => <TableHeader>{t('futures.market.user.trades.fees')}</TableHeader>,
								accessorKey: 'feesPaid',
								cell: (cellProps) => {
									return cellProps.getValue() ? (
										<Currency.Price price={cellProps.getValue()} colorType="secondary" />
									) : (
										NO_VALUE
									)
								},
								enableSorting: true,
							},
							{
								header: () => <TableHeader>{t('futures.market.user.trades.rpnl')}</TableHeader>,
								accessorKey: 'netPnl',
								cell: (cellProps) => {
									return cellProps.row.original.orderType !== 'Liquidation' ? (
										<Tooltip
											id={`trade-pnl-${cellProps.row.index}`}
											trigger={
												<ToolTipTrigger>
													{wei(cellProps.getValue()).gt(0) && <Invisible>-</Invisible>}
													<Currency.Price price={cellProps.getValue()} colored />
												</ToolTipTrigger>
											}
											content={
												<PnlTooltip data={getTooltipData(cellProps.row.original as TradeHistory)} />
											}
											portal={true}
										/>
									) : (
										NO_VALUE
									)
								},
								enableSorting: true,
							},
							{
								accessorKey: 'committedTxHash',
								header: () => (
									<TableHeader>{t('dashboard.history.trades-table.submission-tx')}</TableHeader>
								),
								cell: (cellProps) => {
									return (
										<Body>
											{cellProps.getValue() ? (
												<StyledExternalLink href={blockExplorer.txLink(cellProps.getValue())}>
													{truncateAddress(cellProps.getValue())}
												</StyledExternalLink>
											) : (
												NO_VALUE
											)}
										</Body>
									)
								},
							},
							{
								accessorKey: 'settlementTxHash',
								header: () => (
									<TableHeader>{t('dashboard.history.trades-table.execution-tx')}</TableHeader>
								),
								cell: (cellProps) => {
									return (
										<Body>
											{cellProps.getValue() ? (
												<StyledExternalLink href={blockExplorer.txLink(cellProps.getValue())}>
													{truncateAddress(cellProps.getValue())}
												</StyledExternalLink>
											) : (
												NO_VALUE
											)}
										</Body>
									)
								},
							},
						]}
						columnsDeps={columnsDeps}
						data={trades}
						isLoading={isLoading}
						noResultsMessage={
							!isLoading && trades?.length === 0 ? (
								<TableNoResults>{t('futures.market.user.trades.no-results')}</TableNoResults>
							) : undefined
						}
					/>
				</TableContainer>
			</DesktopOnlyView>

			<MobileOrTabletView>
				<MobileTableContainer>
					<MobileTableFilters showFilters />
					<MobileTable
						highlightRowsOnHover
						rounded={false}
						noBottom={false}
						autoResetPageIndex={false}
						showPagination={false}
						columns={[
							{
								accessorKey: 'displayAsset',
								cell: (cellProps) => (
									<FlexDivCol>
										<Body>{cellProps.getValue()}</Body>
										<Body color="secondary">
											{formatNumber(cellProps.row.original.amount, {
												suggestDecimals: true,
												removeTrailingZeros: true,
											})}
											{` ${cellProps.row.original.displayAsset}`}
											{cellProps.row.original.orderType === 'Liquidation' ? ' 💀' : ''}
										</Body>
									</FlexDivCol>
								),
							},
							{
								accessorKey: 'side',
								cell: (cellProps) => (
									<FlexDivCol>
										<Body color="secondary">{t('futures.market.user.trades.side')}</Body>
										{cellProps.row.original.side ? (
											<PositionType side={cellProps.row.original.side} />
										) : (
											NO_VALUE
										)}
									</FlexDivCol>
								),
							},
							{
								accessorKey: 'price',
								cell: (cellProps) => (
									<FlexDivCol>
										<Body color="secondary">{t('futures.market.user.trades.price')}</Body>
										<Currency.Price price={cellProps.getValue()} />
									</FlexDivCol>
								),
							},
							{
								accessorKey: 'netPnl',
								cell: (cellProps) =>
									cellProps.row.original.orderType !== 'Liquidation' ? (
										<FlexDivCol>
											<Body color="secondary">{t('futures.market.user.trades.rpnl')}</Body>
											<Tooltip
												id={`trade-pnl-${cellProps.row.index}`}
												trigger={
													<ToolTipTrigger>
														{wei(cellProps.getValue()).gt(0) && <Invisible>-</Invisible>}
														<Currency.Price price={cellProps.getValue()} colored />
													</ToolTipTrigger>
												}
												content={
													<PnlTooltip
														data={getTooltipData(cellProps.row.original as TradeHistory)}
													/>
												}
											/>
										</FlexDivCol>
									) : (
										NO_VALUE
									),
							},
							{
								accessorKey: 'feesPaid',
								cell: (cellProps) => (
									<FlexDivCol>
										<Body color="secondary">{t('futures.market.user.trades.fees')}</Body>
										{cellProps.getValue() ? (
											<Currency.Price price={cellProps.getValue()} />
										) : (
											NO_VALUE
										)}
									</FlexDivCol>
								),
							},
							{
								accessorKey: '_blank',
								cell: (_cellProps) => <> </>,
							},
							{
								accessorKey: 'timestamp',
								cell: (cellProps) => (
									<FlexDivCol>
										<TimeDisplay value={cellProps.getValue()} />
									</FlexDivCol>
								),
							},
							{
								accessorKey: 'txnHash',
								cell: (cellProps) => (
									<FlexDivCol>
										<Body color="secondary">{t('futures.market.user.trades.transaction')}</Body>
										<Body>
											{cellProps.getValue() ? (
												<StyledExternalLink href={blockExplorer.txLink(cellProps.getValue())}>
													{truncateAddress(cellProps.getValue())}
												</StyledExternalLink>
											) : (
												NO_VALUE
											)}
										</Body>
									</FlexDivCol>
								),
							},
						]}
						columnsDeps={columnsDeps}
						data={trades}
						isLoading={isLoading}
						noResultsMessage={
							!isLoading && trades?.length === 0 ? (
								<TableNoResults>{t('futures.market.user.trades.no-results')}</TableNoResults>
							) : undefined
						}
					/>
				</MobileTableContainer>
			</MobileOrTabletView>
		</Container>
	)
})

export default TradesTable

const Container = styled.div`
	display: flex;
	flex-direction: row;
	flex: 1;
	height: 100%;
`
const MobileTableContainer = styled(FlexDivCol)`
	width: 100%;
`
const MobileTable = styled(Table)`
	.table-row:first-child {
		display: none;
	}

	.table-body {
		max-height: 100%;
	}

	.table-body-row:first-child {
		padding-top: 15px;
	}

	.table-body-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-template-rows: auto;
		grid-column-gap: 15px;
		grid-row-gap: 10px;
		padding: 15px 0px;
		margin: 0 20px;
	}

	${TableCell}:first-child,
	${TableCell}:last-child {
		padding: 0;
	}
	${TableCell} {
		height: 100%;
		width: 100% !important;
		display: flex;
		align-items: start;
		justify-content: space-between;
	}
	border: none;
` as typeof Table

const TableContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;

	.table-body-row:not(:last-child) {
		border-bottom: none;
	}
`
const TableNoResults = styled(BaseTableNoResults)`
	display: flex;
	margin-top: -${USER_INFO_FILTERS_HEIGHT}px;
	align-items: center;
	padding: 0;
`

const StyledExternalLink = styled(ExternalLink)`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	text-decoration: underline;
	&:hover {
		text-decoration: underline;
	}
`

const Invisible = styled.span`
	color: transparent;
`
